import type { UserProfile } from '../types/api';

export type InlineStatusTone = 'success' | 'error' | 'info';
export type EmailStatusSummaryTone = InlineStatusTone | 'default';

export interface EmailVerificationPanelState {
  readonly emailText: string;
  readonly statusText: string;
  readonly statusTone: 'verified' | 'required' | 'unverified';
  readonly summaryText: string;
  readonly alertVisible: boolean;
  readonly alertText: string;
  readonly verifyButtonVisible: boolean;
  readonly verifyButtonText: string;
}

export function setInlineStatus(elementId: string, message: string, tone: InlineStatusTone = 'success'): void {
  const element = document.getElementById(elementId);
  if (!element) return;
  const toneClass = tone === 'success'
    ? 'border-emerald-800 bg-emerald-950/20 text-emerald-200'
    : tone === 'error'
      ? 'border-red-800 bg-red-950/20 text-red-200'
      : 'border-zinc-700 bg-zinc-900/60 text-zinc-300';
  element.className = `rounded border px-3 py-2 text-xs ${toneClass}`;
  element.textContent = message;
  element.style.display = '';
}

export function clearInlineStatus(elementId: string): void {
  const element = document.getElementById(elementId);
  if (!element) return;
  element.textContent = '';
  element.style.display = 'none';
}

export function setEmailStatusSummary(message: string, tone: EmailStatusSummaryTone = 'default'): void {
  const summary = document.getElementById('email-status-summary');
  if (!summary) return;
  const toneClass = tone === 'success' ? 'text-emerald-300'
    : tone === 'error' ? 'text-red-300'
    : tone === 'info' ? 'text-zinc-300'
    : 'text-zinc-500';
  summary.className = `mt-1 text-xs ${toneClass}`;
  summary.textContent = message;
}

export function setSubmitButtonBusy(button: HTMLButtonElement | null, busyText: string): () => void {
  if (!button) return () => {};
  const originalHtml = button.innerHTML;
  const originalAriaBusy = button.getAttribute('aria-busy');
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  button.textContent = busyText;
  return function restoreSubmitButton(): void {
    button.disabled = false;
    button.innerHTML = originalHtml;
    if (originalAriaBusy === null) button.removeAttribute('aria-busy');
    else button.setAttribute('aria-busy', originalAriaBusy);
  };
}

export function renderSettingsLoadingState(): void {
  const loading = document.getElementById('settings-loading');
  if (!loading) return;
  loading.textContent = '';
  loading.className = 'text-center py-10';
  loading.style.display = '';
  const icon = document.createElement('i');
  icon.className = 'fas fa-spinner fa-spin text-3xl text-brand-400 mb-3';
  const text = document.createElement('p');
  text.className = 'text-zinc-400';
  text.textContent = 'Loading settings...';
  loading.append(icon, text);
}

export function renderSettingsLoadError(message: string, retry: () => Promise<void>): void {
  const loading = document.getElementById('settings-loading');
  if (!loading) return;
  loading.textContent = '';
  loading.className = 'rounded border border-red-800 bg-red-950/20 p-4 text-sm text-red-200';
  const text = document.createElement('p');
  text.textContent = message;
  const action = document.createElement('button');
  action.type = 'button';
  action.className = 'btn-ghost mt-3 text-xs';
  action.textContent = 'Retry';
  action.addEventListener('click', async () => {
    const restore = setSubmitButtonBusy(action, 'Retrying...');
    renderSettingsLoadingState();
    try {
      await retry();
    } finally {
      restore();
    }
  });
  loading.append(text, action);
}

export function renderAccountFacts(profile: Pick<UserProfile, 'email' | 'role' | 'created_at'>, display: EmailVerificationPanelState): void {
  const email = document.getElementById('user-email');
  if (email) email.textContent = display.emailText;
  const badge = document.getElementById('email-status-badge');
  if (badge) {
    const toneClass = display.statusTone === 'verified'
      ? 'border-emerald-800 text-emerald-300 bg-emerald-950/20'
      : display.statusTone === 'required'
        ? 'border-amber-800 text-amber-300 bg-amber-950/20'
        : 'border-zinc-700 text-zinc-300 bg-zinc-900/60';
    badge.className = `inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${toneClass}`;
    badge.textContent = display.statusText;
  }
  setEmailStatusSummary(display.summaryText);
  const alert = document.getElementById('email-verification-alert');
  if (alert) alert.style.display = display.alertVisible ? '' : 'none';
  const alertText = document.getElementById('email-verification-alert-text');
  if (alertText) alertText.textContent = display.alertText;
  const role = document.getElementById('user-role');
  if (role) role.textContent = profile.role === 'maintainer' ? 'Maintainer' : 'User';
  const memberSince = document.getElementById('member-since');
  if (memberSince) memberSince.textContent = new Date(profile.created_at).toLocaleDateString();
  const verifyButton = document.getElementById('verify-email-btn') as HTMLButtonElement | null;
  if (verifyButton) {
    verifyButton.style.display = display.verifyButtonVisible ? '' : 'none';
    verifyButton.textContent = display.verifyButtonText;
    verifyButton.disabled = !display.verifyButtonVisible;
    verifyButton.removeAttribute('aria-busy');
  }
}
